<template>
  <div class="container-fluid current-sale-line">
    <div class="row" @dblclick="openItem">
      <div class="line-description-pane col-7">
        <label :for="`price-type-${saleLine.index}`" class="mb-3" v-if="canSetPriceLevel">
          Item Price
          <select :id="`price-type-${saleLine.index}`" class="w-100" v-model="saleLine.priceType"
                  @change="recalculateSaleLine">
            <option value="Normal">Normal</option>
            <option value="At Cost">At Cost</option>
            <option value="Price 1">Price 1</option>
            <option value="Price 2">Price 2</option>
            <option value="Price 3">Price 3</option>
            <option value="Price 4">Price 4</option>
            <option value="Price 5">Price 5</option>
            <option value="Custom">Custom</option>
          </select>
        </label>

        <h6 class="line-description d-flex flex-column">
          <span class="text-sm text-secondary">{{ saleLine.Plu }}</span>
          {{ $utils.toPascalCase(saleLine.Description) }}<br>
        </h6>



        <button type="button" id="discount-btn" class="btn btn-outline-info text-sm mx-2 mb-2" @click="discount">
          Add Discount
          <font-awesome-icon icon="percent"/>
        </button>

        <button type="button" style="display: none" data-bs-toggle="modal" :id="`discountCheck-${saleLine.index}`"
                :data-bs-target="`#discountModal-${saleLine.index}`"></button>
        <div class="modal fade" :id="`discountModal-${saleLine.index}`" tabindex="-1"
             :aria-labelledby="`discountModal-${saleLine.index}Label`" aria-hidden="true">
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title" :id="`discountModal-${saleLine.index}Label`">
                  Apply Discount to {{ $utils.toPascalCase($utils.cutLength(saleLine.Description, 30)) }}
                </h5>
                <button type="button" class="btn-close" :id="`closeBTN-${saleLine.index}`" data-bs-dismiss="modal"
                        aria-label="Close"></button>
              </div>
              <form>
                <div class="d-flex flex-row p-1 justify-content-evenly mt-2">
                  <label>
                    <input class="form-check-input" type="radio" value="value" v-model="discountType">
                    Value Discount
                  </label>
                  <label>
                    <input class="form-check-input" type="radio" value="percent" v-model="discountType">
                    Percentage Discount
                  </label>
                </div>
                <hr class="mx-2"/>
                <label class="row mx-3 mb-2">
                  Discount Amount
                  <input type="number" class="form-control" v-model="discountAmount">
                </label>
              </form>
              <div class="modal-footer mt-3">
                <a class="btn btn-secondary" data-bs-dismiss="modal">Close</a>
                <a class="btn btn-primary" @click="applyDiscount">Save changes</a>
              </div>
            </div>
          </div>
        </div>

        <button @click="voidItem(saleLine)">
          <font-awesome-icon icon="fa-solid fa-trash" style="color: red"/>
        </button>
        <h5 class="text-success text-sm" v-if="saleLine.Discount>0">{{
            `Discount ${$utils.formatCurrency(saleLine.Discount, 2, debtorCurrency.Symbol)}`
          }}</h5>
      </div>
      <div class="d-flex flex-column justify-content-between line-prices-pane col-5">
        <CurrentSaleQty :saleLine="saleLine"/>
        <div class="price-row">
          <!-- Unit price and Total are both rendered at 4 dp so
               server-supplied values like 306.1727 display as
               K 306.1727 (no spurious rounding). The wire payload
               to processInvoice still carries Line at 2 dp money
               (rounded in the Vuex addToCurrentSaleLines mutation). -->
          <p class="price-label mx-2">Unit Price: <span
              class="line-price">{{
              $utils.formatCurrency(saleLine.Unit / debtorCurrency.Rate, 4, debtorCurrency.Symbol)
            }}</span></p>
          <p class="price-label">Total: <span
              class="line-price">{{
              $utils.formatCurrency(((saleLine.Unit * saleLine.Qty) / debtorCurrency.Rate) - saleLine.Discount, 4, debtorCurrency.Symbol)
            }}</span>
          </p>
        </div>
      </div>
    </div>
    <custom-modal :ref="`saleline-item-`+saleLine.index" size="md"
                  :title="`Update item Price ${$utils.toPascalCase(saleLine.Description)}`">
      <div class="d-flex flex-column">
        <label for="itemPrice">Unit Price</label>
        <input type="number" step="0.01" id="itemPrice" @input="$store.commit(`updateSaleLinePrice`, saleLine)"
               class="form-control"
               v-model.number="saleLine.Unit">

        <label for="itemQty">Qty</label>
        <input type="number" step="0.0001" id="itemQty"
               @focus="captureModalQty"
               @input="onModalQtyInput"
               class="form-control mb-1" v-model.number="saleLine.Qty">

        <div v-if="saleLine.Discount < 0">
          <span class="text-primary mb-1">Line Before Discount : {{
              $utils.formatMoney(saleLine.Line + saleLine.Discount)
            }}</span>
          <span class="text-success mb-1">Discount : {{ $utils.formatMoney(saleLine.Discount) }}</span>
        </div>

        <label for="itemLinePrice">Line Price</label>
        <input type="number" @input="updateItemPrice" step="0.01" id="itemLinePrice" class="form-control"
               v-model.number="saleLine.Line">
      </div>
    </custom-modal>
    <div class="row" v-if="hasInstructions">
      <ul class="cooking-instruction-list">
        <li class="cooking-instruction-list-item" v-for="instruction in instructions">
          {{ instruction.CookingMsg }} <span>{{ instruction.count }} X {{
            $utils.formatCurrency(instruction.Price, 2, debtorCurrency.Symbol)
          }}</span>
          Total: {{ $utils.formatCurrency(instruction.count * instruction.Price, 2, debtorCurrency.Symbol) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import CurrentSaleQty from "@/components/point-of-sale/sales/CurrentSaleQty";
import CustomModal from "@/components/layout/utils/CustomModal";

export default {
  name: "CurrentSaleLine",
  components: {CustomModal, CurrentSaleQty},
  async mounted() {
    this.canSetPriceLevel = await this.$store.dispatch(`validateSecurityPermissionNoPrompt`, `PriceLevel`);
  },
  data() {
    return {
      discountType: 'value',
      discountAmount: 0.00,
      itemPrice: 0.00,
      canSetPriceLevel : false,
      previousModalQty: null,
    }
  },
  props: {
    saleLine: {
      type: Object,
      required: true
    }
  },
  watch:{
    'saleLine.Unit'(nv,ov){
      console.log({nv})
      console.log(this.debtorCurrency)
    }
  },
  computed: {
    hasInstructions() {
      return (this.saleLine.instructions && this.saleLine.instructions.length > 0)
    },

    debtorCurrency() {
      return this.$store.getters.getDebtorCurrency;
    },
    instructionTotal() {
      if (!this.saleLine.instructions || this.saleLine.instructions.length === 0) return 0.00;
      let total = this.saleLine.instructions.map(i => i.Price).reduce((acc, n) => Number(n) + Number(acc))
      if (isNaN(total)) {
        return 0.00;
      }
      return Number(total.toFixed(2))
    },
    instructions() {
      return this.saleLine.instructions.map((int) => {
        int.Price = Number(int.Price.toFixed(2))
        return int;
      });
    }
  },
  methods: {
    voidItem(item) {

      this.$store.commit('voidLine', item)
    },
    recalculateSaleLine() {
      if (this.saleLine.priceType === `Custom`) return this.openItem();
      this.$store.commit(`updateSaleLinePrice`, this.saleLine)
    },
    discount() {
      let isAllowed = this.$store.dispatch(`validateSecurityPermission`, `Discount`)
      if (!isAllowed) return;
      document.getElementById(`discountCheck-${this.saleLine.index}`).click();
    },
    applyDiscount() {
      let type = this.discountType;
      let line = Number(this.saleLine.Unit) * Number(this.saleLine.Qty);
      let discount = Number(this.discountAmount);
      if (type === `percent`) discount = (this.discountAmount / 100) * line;
      this.saleLine.Discount = discount;
      this.saleLine.Line = Number(this.saleLine.Line) - discount;
      document.getElementById(`closeBTN-${this.saleLine.index}`).click();
    },
    async openItem() {
      let isAllowed = await this.$store.dispatch(`validateSecurityPermissionNoPrompt`,`PriceChange`)
      if (!isAllowed) return;
      this.saleLine.priceType = `Custom`;
      this.$store.commit(`updateSaleLinePrice`, this.saleLine)
      return this.$refs[`saleline-item-${this.saleLine.index}`].openModal()
    },
    updateItemPrice() {
      this.saleLine.Unit = (this.saleLine.Line + this.saleLine.Discount) / this.saleLine.Qty;
    },
    captureModalQty() {
      this.previousModalQty = Number(this.saleLine.Qty);
    },
    onModalQtyInput(event) {
      let rawQty = event && event.target ? event.target.value : this.saleLine.Qty;
      let newQty = Number(rawQty);
      if (rawQty === '' || !Number.isFinite(newQty) || newQty < 0) {
        return;
      }
      // Same 4-dp normalisation as the inline CurrentSaleQty input: the
      // modal lets the user type up to step="0.0001", the model collapses
      // to 4 dp so the value matches what the cart, the held table, and
      // the invoice payload all see.
      newQty = this.$utils.roundTo(newQty, 4);
      let oldQty = Number(this.previousModalQty);
      if (!Number.isFinite(oldQty)) {
        oldQty = newQty;
      }
      this.$store.commit('syncSaleLineInstructionsWithQty', {
        index: this.saleLine.index,
        oldQty: oldQty,
        newQty: newQty,
      })
      // Recalculate Line total preserving the existing Unit price
      // Do NOT call updateSaleLinePrice here as it re-fetches the stock price
      // which resets manually set prices back to the database value (e.g., K 0)
      this.saleLine.Qty = newQty;
      // Round the Line money value to 2 dp so the cart and the server
      // see the same clean value (no IEEE-754 noise like 0.48840500000000003).
      let lineTotal = this.$utils.roundTo(Number(this.saleLine.Unit) * newQty, 2)
                    - Number(this.saleLine.Discount || 0);
      this.saleLine.Line = this.$utils.roundTo(lineTotal, 2);
      this.previousModalQty = newQty;
    },
  }
}
</script>

<style lang="scss" scoped>
.current-sale-line {
  padding: 10px;
  min-height: 95px;
  margin-bottom: 5px;

  .row {
    .line-description-pane {
      .line-description {
        font-size: 18px;
        margin: 0;
        line-height: 18px;
        font-weight: 500;
      }
    }

    .line-prices-pane {
      .price-label {
        font-size: 12px;
        margin: 0;

        .line-price {
          font-size: 15px;
          font-weight: 700;
        }
      }
    }
  }
}

button {
  background-color: transparent;
}

.cooking-instruction-list {
  list-style: inside;

  .cooking-instruction-list-item {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #404040;
    display: flex;
    justify-content: space-between;
  }
}

.price-row {
  display: flex;
  flex-direction: row;
}

label.mb-3 {
  border-radius: 8px !important;
}

.text-sm {
  font-size: 12px;
}

#discount-btn {
  padding: 2px;
  border-radius: 5px;
}
</style>
